import React from 'react';
import { useApp } from './context/App';
import { AppRoutes } from './routes';
import { ToolBar } from './components/layout/ToolBar';

import { CookieBanner } from './components/ui/CookieConsent/CookieConsent';
import {
    BrowserRouter as Router,
    useLocation
} from "react-router-dom"
import { AnimatePresence, motion } from 'framer-motion';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { OfflineFeedback } from './components/ui/OfflineFeedback';

import { Header } from './components/layout/Header';
import { HeaderMobile } from './components/layout/HeaderMobile';
import { Footer } from './components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import VLibras from '@djpfs/react-vlibras';

function AnimatedApp() {
    const { visible, mobile } = useApp();
    const location = useLocation();

    // The canonical URL should strictly drop any query parameters to avoid SEO parameter injection spam
    const canonicalUrl = `https://itajai.sc.gov.br${location.pathname}`;

    // Helper to determine breadcrumb title for common layout
    const getRouteTitle = (pathname: string) => {
        if (pathname === '/') return undefined;
        if (pathname.startsWith('/noticias')) return 'Notícias';
        if (pathname.startsWith('/a/')) return 'Artigo';
        if (pathname.startsWith('/e/')) return 'Entidade';
        if (pathname.startsWith('/contato') || pathname.startsWith('/telefones')) return 'Contato';
        if (pathname.startsWith('/localizacao')) return 'Localização';
        if (pathname.startsWith('/busca')) return 'Busca';
        if (pathname.startsWith('/videos')) return 'Vídeos';
        if (pathname === '/iptu') return 'IPTU';
        return undefined;
    };

    const routeTitle = getRouteTitle(location.pathname);
    const showSearch = location.pathname !== '/';

    return (
        <div className="flex flex-col min-h-screen relative overflow-x-hidden bg-white dark:bg-surface-dark transition-colors duration-300 w-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
            <Helmet>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            {/* Barra física para a Safe Area do iOS - força Safari a computar a cor como Azul Escuro, e impede espaços em branco vazados */}
            <div className="fixed top-0 left-0 w-full h-[env(safe-area-inset-top)] bg-primary-dark dark:bg-gray-900 z-[9999] pointer-events-none" />

            <ToolBar />

            {mobile ? (
                <HeaderMobile route={routeTitle} />
            ) : (
                <Header search={showSearch} route={routeTitle} />
            )}

            <main id="main-content" className="outline-none flex-grow">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={location.pathname}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="w-full flex-grow flex flex-col"
                    >
                        <ErrorBoundary key={location.pathname}>
                            <AppRoutes />
                        </ErrorBoundary>
                    </motion.div>
                </AnimatePresence>
            </main>

            <Footer />
            {visible && <VLibras forceOnload />}

            <CookieBanner />
            <OfflineFeedback />
        </div>
    );
}

export function App() {
    const { basename } = useApp()

    return (
        <Router
            basename={basename}
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-blue-600 focus:border-2 focus:border-blue-600 focus:rounded-lg focus:shadow-xl focus:outline-none"
            >
                Pular para o conteúdo principal
            </a>

            <AnimatedApp />
        </Router>
    )
}